import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import clsx from 'clsx'
import { X } from 'lucide-react'
import { useLibrary } from '@/hooks/coaching/useLibrary'
import { useUpdateUnitAssessmentPlacement } from '@/hooks/coaching/useUnitAssessments'
import { buildAnchorOptions } from './CreateUnitAssessmentModal'
import { LessonPlacementPicker } from './LessonPlacementPicker'

interface Props {
  learnerId: number
  unitAssessmentId: number
  initialAnchorLessonId: number
  onClose: () => void
}

/** 응시 시작 전 단원평가 특강의 진행 위치만 변경한다. */
export function UnitAssessmentPlacementDialog({
  learnerId,
  unitAssessmentId,
  initialAnchorLessonId,
  onClose,
}: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const library = useLibrary(learnerId)
  const updatePlacement = useUpdateUnitAssessmentPlacement(learnerId, unitAssessmentId)
  const options = useMemo(() => buildAnchorOptions(library.data?.tree ?? []), [library.data])
  const [anchorLessonId, setAnchorLessonId] = useState(initialAnchorLessonId)
  const [error, setError] = useState<string | null>(null)
  const effectiveAnchorLessonId = options.some((option) => option.lessonId === anchorLessonId)
    ? anchorLessonId
    : options[0]?.lessonId ?? null

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const submit = async () => {
    if (effectiveAnchorLessonId == null) return
    setError(null)
    try {
      await updatePlacement.mutateAsync({ anchorLessonId: effectiveAnchorLessonId })
      onClose()
    } catch (requestError) {
      const code = axios.isAxiosError(requestError)
        ? requestError.response?.data?.error?.code
        : null
      setError(code
        ? t(`common:apiError.${code}`, t('common:error.unknown'))
        : t('common:error.unknown'))
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('coaching:unitAssessment.placement.title')}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
      >
        <header className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {t('coaching:unitAssessment.placement.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('coaching:unitAssessment.placement.description')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('coaching:unitAssessment.placement.close')}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="size-5" />
          </button>
        </header>

        {library.isLoading ? (
          <p className="py-6 text-center text-sm text-gray-500">
            {t('coaching:unitAssessment.placement.loading')}
          </p>
        ) : options.length === 0 ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800" role="status">
            {t('coaching:unitAssessment.placement.noAnchor')}
          </p>
        ) : (
          <LessonPlacementPicker
            id="unit-assessment-placement-anchor"
            label={t('coaching:unitAssessment.create.anchorLesson')}
            options={options}
            value={effectiveAnchorLessonId}
            onChange={setAnchorLessonId}
          />
        )}

        {error && <p className="mt-3 text-sm text-red-600" role="alert">{error}</p>}

        <footer className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('coaching:unitAssessment.placement.cancel')}
          </button>
          <button
            type="button"
            disabled={
              effectiveAnchorLessonId == null ||
              effectiveAnchorLessonId === initialAnchorLessonId ||
              updatePlacement.isPending
            }
            onClick={() => void submit()}
            className={clsx(
              'rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white',
              (
                effectiveAnchorLessonId == null ||
                effectiveAnchorLessonId === initialAnchorLessonId ||
                updatePlacement.isPending
              ) && 'cursor-not-allowed opacity-50',
            )}
          >
            {updatePlacement.isPending
              ? t('coaching:unitAssessment.placement.saving')
              : t('coaching:unitAssessment.change')}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
